import { createLocalDocument, type LocalDocument } from "./documents";

const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3";
const DRIVE_UPLOAD_BASE = "https://www.googleapis.com/upload/drive/v3";
const DOCUMENT_MIME_TYPE = "application/json";
const FOLDER_MIME_TYPE = "application/vnd.google-apps.folder";
const APP_PROPERTY_KEY = "whisperwriter";
const DOCUMENT_PROPERTY_VALUE = "document";
const FOLDER_PROPERTY_VALUE = "folder";
const DOCUMENT_FORMAT_VERSION = "1";
const DRIVE_FOLDER_NAME = "WhisperWriter";

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken(options?: { prompt?: "" | "consent" | "select_account" }): void;
}

interface GoogleAccountsOAuth2 {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }): GoogleTokenClient;
}

interface DriveFileMetadata {
  id: string;
  name?: string;
  createdTime?: string;
  modifiedTime?: string;
  appProperties?: Record<string, string>;
}

interface DriveFileListResponse {
  files?: DriveFileMetadata[];
  nextPageToken?: string;
}

interface DriveErrorResponse {
  error?: {
    message?: string;
    status?: string;
  };
}

interface DriveDocumentPayload {
  format: "whisperwriter-document";
  version: number;
  document: Omit<LocalDocument, "driveFileId">;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: GoogleAccountsOAuth2;
      };
    };
  }
}

let googleIdentityScriptPromise: Promise<void> | null = null;
let accessToken = "";
let accessTokenExpiresAt = 0;
let driveFolderId = "";

export function isGoogleDriveConfigured(clientId: string): boolean {
  return Boolean(clientId.trim());
}

export async function requestDriveAccess(clientId: string): Promise<void> {
  if (!isGoogleDriveConfigured(clientId)) {
    throw new Error("Google Drive is not configured.");
  }

  await loadGoogleIdentityScript();

  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) {
    throw new Error("Google Drive sign-in is unavailable.");
  }

  return new Promise((resolve, reject) => {
    const client = oauth.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }

        if (!response.access_token) {
          reject(new Error("Google Drive did not return an access token."));
          return;
        }

        accessToken = response.access_token;
        accessTokenExpiresAt = Date.now() + Math.max(0, (response.expires_in ?? 3600) - 60) * 1000;
        resolve();
      },
      error_callback: (error) => {
        reject(new Error(error.message || error.type || "Google Drive sign-in was cancelled."));
      }
    });

    client.requestAccessToken({
      prompt: accessToken && Date.now() < accessTokenExpiresAt ? "" : "consent"
    });
  });
}

export async function listDriveDocuments(): Promise<LocalDocument[]> {
  await ensureDriveFolder();

  const files: DriveFileMetadata[] = [];
  let pageToken = "";

  do {
    const params = new URLSearchParams({
      fields: "nextPageToken,files(id,name,createdTime,modifiedTime,appProperties)",
      pageSize: "100",
      q: `appProperties has { key='${APP_PROPERTY_KEY}' and value='${DOCUMENT_PROPERTY_VALUE}' } and trashed = false`,
      spaces: "drive"
    });

    if (pageToken) {
      params.set("pageToken", pageToken);
    }

    const response = await driveRequest<DriveFileListResponse>(
      `${DRIVE_API_BASE}/files?${params.toString()}`
    );

    files.push(...(response.files ?? []));
    pageToken = response.nextPageToken ?? "";
  } while (pageToken);

  const documents = await Promise.all(files.map(downloadDriveDocument));

  return documents
    .filter((document): document is LocalDocument => Boolean(document))
    .sort((left, right) => right.updatedAt - left.updatedAt);
}

export async function saveDriveDocument(document: LocalDocument): Promise<LocalDocument> {
  const folderId = await ensureDriveFolder();
  const normalizedDocument = createLocalDocument(document);
  const metadata: Record<string, unknown> = {
    name: getDriveFileName(normalizedDocument),
    mimeType: DOCUMENT_MIME_TYPE,
    appProperties: {
      [APP_PROPERTY_KEY]: DOCUMENT_PROPERTY_VALUE,
      documentId: normalizedDocument.id,
      version: DOCUMENT_FORMAT_VERSION
    }
  };

  if (!normalizedDocument.driveFileId) {
    metadata.parents = [folderId];
  }

  const payload = serializeDriveDocument(normalizedDocument);
  const fileMetadata = await uploadMultipart<DriveFileMetadata>(
    normalizedDocument.driveFileId
      ? `${DRIVE_UPLOAD_BASE}/files/${encodeURIComponent(normalizedDocument.driveFileId)}?uploadType=multipart&fields=id,name,createdTime,modifiedTime,appProperties`
      : `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&fields=id,name,createdTime,modifiedTime,appProperties`,
    normalizedDocument.driveFileId ? "PATCH" : "POST",
    metadata,
    payload
  );

  return createLocalDocument({
    ...normalizedDocument,
    driveFileId: fileMetadata.id,
    createdAt: parseDriveTime(fileMetadata.createdTime, normalizedDocument.createdAt),
    updatedAt: parseDriveTime(fileMetadata.modifiedTime, normalizedDocument.updatedAt)
  });
}

export async function deleteDriveDocument(document: LocalDocument): Promise<void> {
  if (!document.driveFileId) {
    return;
  }

  await driveRequest<void>(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(document.driveFileId)}`,
    { method: "DELETE" }
  );
}

export async function deleteDriveDocuments(documents: LocalDocument[]): Promise<void> {
  await Promise.all(documents.map(deleteDriveDocument));
}

async function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return;
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google sign-in failed to load.")), {
        once: true
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google sign-in failed to load."));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

async function ensureDriveFolder(): Promise<string> {
  if (driveFolderId) {
    return driveFolderId;
  }

  const params = new URLSearchParams({
    fields: "files(id,name,createdTime,modifiedTime,appProperties)",
    pageSize: "1",
    q: `mimeType = '${FOLDER_MIME_TYPE}' and appProperties has { key='${APP_PROPERTY_KEY}' and value='${FOLDER_PROPERTY_VALUE}' } and trashed = false`,
    spaces: "drive"
  });

  const existingFolder = await driveRequest<DriveFileListResponse>(
    `${DRIVE_API_BASE}/files?${params.toString()}`
  );
  const firstFolder = existingFolder.files?.[0];

  if (firstFolder?.id) {
    driveFolderId = firstFolder.id;
    return driveFolderId;
  }

  const createdFolder = await driveRequest<DriveFileMetadata>(`${DRIVE_API_BASE}/files?fields=id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=utf-8"
    },
    body: JSON.stringify({
      name: DRIVE_FOLDER_NAME,
      mimeType: FOLDER_MIME_TYPE,
      appProperties: {
        [APP_PROPERTY_KEY]: FOLDER_PROPERTY_VALUE,
        version: DOCUMENT_FORMAT_VERSION
      }
    })
  });

  driveFolderId = createdFolder.id;
  return driveFolderId;
}

async function downloadDriveDocument(file: DriveFileMetadata): Promise<LocalDocument | null> {
  try {
    const payload = await driveRequest<DriveDocumentPayload>(
      `${DRIVE_API_BASE}/files/${encodeURIComponent(file.id)}?alt=media`
    );
    const document = payload.document;

    return createLocalDocument({
      ...document,
      driveFileId: file.id,
      createdAt: parseDriveTime(file.createdTime, document.createdAt),
      updatedAt: Math.max(
        document.updatedAt,
        parseDriveTime(file.modifiedTime, document.updatedAt)
      )
    });
  } catch {
    return null;
  }
}

async function uploadMultipart<T>(
  url: string,
  method: "POST" | "PATCH",
  metadata: Record<string, unknown>,
  payload: DriveDocumentPayload
): Promise<T> {
  const boundary = `whisperwriter_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const body = new Blob(
    [
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\nContent-Type: ${DOCUMENT_MIME_TYPE}; charset=UTF-8\r\n\r\n`,
      JSON.stringify(payload, null, 2),
      `\r\n--${boundary}--`
    ],
    { type: `multipart/related; boundary=${boundary}` }
  );

  return driveRequest<T>(url, {
    method,
    headers: {
      "Content-Type": `multipart/related; boundary=${boundary}`
    },
    body
  });
}

async function driveRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  if (!accessToken || Date.now() >= accessTokenExpiresAt) {
    throw new Error("Google Drive is not connected.");
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);

  const response = await fetch(url, {
    ...init,
    headers
  });

  if (response.status === 401) {
    accessToken = "";
    accessTokenExpiresAt = 0;
  }

  if (!response.ok) {
    throw new Error(await getDriveErrorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function getDriveErrorMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as DriveErrorResponse;
    return payload.error?.message || `Google Drive request failed (${response.status}).`;
  } catch {
    return `Google Drive request failed (${response.status}).`;
  }
}

function serializeDriveDocument(document: LocalDocument): DriveDocumentPayload {
  const { driveFileId: _driveFileId, ...documentPayload } = document;

  return {
    format: "whisperwriter-document",
    version: 1,
    document: documentPayload
  };
}

function getDriveFileName(document: LocalDocument): string {
  const title = document.title.trim() || "Untitled";
  const safeTitle = title.replace(/[^\w .-]+/g, "").replace(/\s+/g, " ").trim() || "Untitled";

  return `${safeTitle}.whisperwriter.json`;
}

function parseDriveTime(value: string | undefined, fallback: number): number {
  if (!value) {
    return fallback;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : fallback;
}
